import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import {
  DEFAULT_PREFS,
  getStoredConsent,
  setStoredConsent,
  clearConsent,
} from "../lib/cookieConsent";

const CookieConsentContext = createContext({
  prefs: DEFAULT_PREFS,
  hasConsented: false,
  acceptAll: () => {},
  rejectNonEssential: () => {},
  savePrefs: () => {},
  reset: () => {},
});

export function CookieConsentProvider({ children }) {
  // Read stored consent synchronously via lazy initializers so the choice is
  // known before the first render — no banner flash for returning visitors.
  const [stored] = useState(() => getStoredConsent());
  const [prefs, setPrefs] = useState(() =>
    stored
      ? {
          necessary: true,
          functional: stored.functional,
          analytics: stored.analytics,
          marketing: stored.marketing,
        }
      : DEFAULT_PREFS,
  );
  const [hasConsented, setHasConsented] = useState(() => !!stored);

  const persist = useCallback((next) => {
    const saved = setStoredConsent(next);
    setPrefs({
      necessary: true,
      functional: !!saved?.functional,
      analytics: !!saved?.analytics,
      marketing: !!saved?.marketing,
    });
    setHasConsented(true);
  }, []);

  const acceptAll = useCallback(() => {
    persist({ functional: true, analytics: true, marketing: true });
  }, [persist]);

  const rejectNonEssential = useCallback(() => {
    persist({ functional: false, analytics: false, marketing: false });
  }, [persist]);

  const savePrefs = useCallback((next) => {
    persist({
      functional: !!next?.functional,
      analytics: !!next?.analytics,
      marketing: !!next?.marketing,
    });
  }, [persist]);

  const reset = useCallback(() => {
    clearConsent();
    setPrefs(DEFAULT_PREFS);
    setHasConsented(false);
  }, []);

  const value = useMemo(
    () => ({ prefs, hasConsented, acceptAll, rejectNonEssential, savePrefs, reset }),
    [prefs, hasConsented, acceptAll, rejectNonEssential, savePrefs, reset],
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent() {
  return useContext(CookieConsentContext);
}
